/**
 * Découpage du texte d'une étape en fragments typés (US-39).
 *
 * L'audit du 8 août 2026 notait que l'unique manifestation d'US-08 à l'écran
 * était une matrice de cases à cocher, depuis supprimée. La table
 * `step_ingredient` est restée, peuplée à chaque écriture — mais plus rien ne
 * l'exposait. Ce module la rend enfin visible là où le regard est déjà : dans
 * le texte de l'instruction, et non dans une liste à côté.
 *
 * **Fonction pure, aucun accès base, aucun rendu.** Elle prend une instruction
 * et les noms normalisés des ingrédients associés, et rend une suite de
 * fragments que le client habille. Le découpage est un parseur de plus, donc
 * unitaire et testé au sens du §1 — c'est le troisième endroit où une erreur
 * serait fausse mais crédible.
 *
 * **L'invariant qui rend le module vérifiable :** la concaténation des
 * fragments rend exactement l'instruction d'origine, caractère pour caractère.
 * Rien ne se perd, rien ne se duplique, rien ne se réécrit. Tout le reste — la
 * priorité des types, le traitement des chevauchements — n'est que la façon de
 * le respecter.
 */
import { extractDureeSecondes } from './extractDuration';
import { extractTemperatureFromText } from './extractTemperature';

export type FragmentKind = 'texte' | 'ingredient' | 'duree' | 'temperature';

export interface StepFragment {
  kind: FragmentKind;
  /** Texte tel qu'il apparaît dans l'instruction. Jamais réécrit. */
  text: string;
  /**
   * Valeur exploitable du fragment : minutes pour `duree`, degrés Celsius pour
   * `temperature`, nom normalisé pour `ingredient`. Absente sur `texte`.
   */
  value?: number | string;
}

const COMBINING_DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

/**
 * Même repli que `matchStepIngredients` et que la recherche : minuscules, sans
 * accent, ligatures défaites. Il doit rester identique aux autres, sinon
 * « œuf » ne reconnaîtrait pas « oeuf » — et la correspondance se ferait sur
 * une forme que personne d'autre n'emploie.
 *
 * **La longueur est préservée** : chaque remplacement rend autant de
 * caractères qu'il en consomme, `œ` mis à part, traité en premier et compté.
 * C'est ce qui permet de reporter les positions trouvées sur le texte
 * d'origine sans décalage.
 */
function normaliserAvecPositions(texte: string): { normalise: string; positions: number[] } {
  const sortie: string[] = [];
  const positions: number[] = [];
  for (let i = 0; i < texte.length; i += 1) {
    const brut = texte[i] ?? '';
    // Les ligatures rendent deux caractères pour un : les deux pointent vers
    // la même position d'origine, ce qui est exactement ce qu'il faut pour
    // retrouver le fragment à surligner.
    const remplace = brut === 'œ' || brut === 'Œ' ? 'oe' : brut === 'æ' || brut === 'Æ' ? 'ae' : brut;
    const dep = remplace.normalize('NFD').replace(COMBINING_DIACRITICS, '').toLowerCase();
    // Un caractère qui disparaît entièrement à la normalisation (un accent
    // combinant isolé) ne doit pas décaler la suite.
    for (const c of dep) { sortie.push(c); positions.push(i); }
  }
  return { normalise: sortie.join(''), positions };
}

function echapper(valeur: string): string {
  return valeur.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

interface Marque { debut: number; fin: number; kind: FragmentKind; value: number | string }

/**
 * Motifs de durée et de température, repérés sur le texte d'origine. Ils sont
 * volontairement plus larges que ceux des extracteurs : ici on cherche
 * *l'étendue à surligner*, la valeur étant ensuite confiée à l'extracteur, seul
 * juge de ce qui est plausible.
 */
const MOTIF_TEMPERATURE = /\d{1,3}\s*(?:°\s*C?|degr[ée]s?)|(?:thermostats?|th\.?)\s*\d{1,2}\b/gi;
const MOTIF_DUREE = /\d{1,2}\s*h(?:eures?)?(?![a-zà-ÿ])\.?(?:\s*\d{1,2}(?:\s*(?:min|mn|minutes?)\b)?)?|\d{1,4}\s*(?:min|mn|minutes?)\b|\d{1,4}\s*(?:s|sec|secs|secondes?)\b/gi;

/**
 * Découpe l'instruction. `ingredientsNormalises` vient de
 * `ingredient.name_normalized` des seuls ingrédients associés à l'étape
 * (`step_ingredient`) : ce module ne devine aucune association, il affiche
 * celles qui existent.
 */
export function splitStepText(
  instruction: string,
  ingredientsNormalises: readonly string[] = [],
): StepFragment[] {
  if (instruction.length === 0) return [];

  const { normalise, positions } = normaliserAvecPositions(instruction);
  const marques: Marque[] = [];

  /** Convertit une étendue trouvée sur la forme normalisée en étendue d'origine. */
  function versOrigine(debutN: number, finN: number): { debut: number; fin: number } | null {
    if (debutN >= positions.length) return null;
    const debut = positions[debutN] ?? 0;
    const dernier = positions[finN - 1];
    return { debut, fin: (dernier ?? debut) + 1 };
  }

  // 1. Températures d'abord : elles exigent un marqueur explicite, donc elles
  //    ne se trompent jamais, et elles doivent gagner sur les durées quand les
  //    deux se disputent le même nombre.
  for (const m of instruction.matchAll(MOTIF_TEMPERATURE)) {
    const valeur = extractTemperatureFromText(m[0]);
    if (valeur !== null && m.index !== undefined) {
      marques.push({ debut: m.index, fin: m.index + m[0].length, kind: 'temperature', value: valeur });
    }
  }

  // 2. Durées. **En secondes depuis US-98** : « Mixer 20 sec » devient un
  //    fragment cliquable comme « Mijoter 30 min », et c'est la moitié du
  //    fonds Thermomix qui gagne son minuteur d'un mot.
  for (const m of instruction.matchAll(MOTIF_DUREE)) {
    const valeur = extractDureeSecondes(m[0]);
    if (valeur !== null && m.index !== undefined) {
      marques.push({ debut: m.index, fin: m.index + m[0].length, kind: 'duree', value: valeur });
    }
  }

  // 3. Ingrédients, sur la forme normalisée, par mots entiers. Le pluriel usuel
  //    est absorbé par un `s` optionnel, même règle que `matchStepIngredients` —
  //    « Ajouter les carottes » reconnaît « carotte ».
  for (const nom of ingredientsNormalises) {
    const propre = nom.trim();
    if (propre.length === 0) continue;
    const motif = new RegExp(`(?<![\\p{L}\\p{N}])${echapper(propre)}s?(?![\\p{L}\\p{N}])`, 'giu');
    for (const m of normalise.matchAll(motif)) {
      if (m.index === undefined) continue;
      const etendue = versOrigine(m.index, m.index + m[0].length);
      if (etendue !== null) marques.push({ ...etendue, kind: 'ingredient', value: propre });
    }
  }

  if (marques.length === 0) return [{ kind: 'texte', text: instruction }];

  // 4. Résolution des chevauchements. Tri par position, puis par étendue
  //    décroissante : entre « sel fin » et « sel », le plus long gagne, sinon
  //    on surlignerait « sel » et laisserait « fin » orphelin. À position et
  //    longueur égales, la température prime (voir 1).
  const ordre: Record<FragmentKind, number> = { temperature: 0, duree: 1, ingredient: 2, texte: 3 };
  marques.sort((a, b) => a.debut - b.debut || (b.fin - b.debut) - (a.fin - a.debut) || ordre[a.kind] - ordre[b.kind]);

  const retenues: Marque[] = [];
  let finPrecedente = 0;
  for (const marque of marques) {
    // Une marque qui commence avant la fin de la précédente est écartée
    // entièrement : la tronquer produirait un fragment dont le texte ne veut
    // plus rien dire.
    if (marque.debut < finPrecedente) continue;
    retenues.push(marque);
    finPrecedente = marque.fin;
  }

  // 5. Assemblage. Le texte entre deux marques, avant la première et après la
  //    dernière, est conservé tel quel — c'est ce qui garantit l'invariant.
  const fragments: StepFragment[] = [];
  let curseur = 0;
  for (const marque of retenues) {
    if (marque.debut > curseur) {
      fragments.push({ kind: 'texte', text: instruction.slice(curseur, marque.debut) });
    }
    fragments.push({ kind: marque.kind, text: instruction.slice(marque.debut, marque.fin), value: marque.value });
    curseur = marque.fin;
  }
  if (curseur < instruction.length) {
    fragments.push({ kind: 'texte', text: instruction.slice(curseur) });
  }
  return fragments;
}
